import { useTranslation } from "react-i18next";

import type { StageSnapshot } from "@msc/shared";

import { sanitizeForDisplay } from "../../lib/customerView/richHtml.js";
import { PauseSlide } from "./PauseSlide.js";

/**
 * Eine Agenda-Folie: Titel groß, darunter der aus Markdown gerenderte Text
 * der Vorlage (Neuigkeiten, Wartungstabelle oder Budget — je nach Punkt).
 *
 * `bodyHtml` kommt im Snapshot bereits aus Markdown gerendert und dort einmal
 * sanitisiert (`renderAgendaMarkdown` beim Aufbau des Snapshots im Cockpit).
 * Die Bühne führt denselben Riegel (`sanitizeForDisplay`, DOMPurify) ein
 * zweites Mal unmittelbar vor dem Einsetzen — dieselbe Regel wie beim
 * Ticket-Verlauf in `TicketSlide.tsx`: Die Bühne ist das Fenster, in das der
 * Kunde schaut, und sie setzt nichts ungeprüft ein.
 *
 * Ein Index außerhalb von `agendaItems` (der Snapshot ist kurz inkonsistent,
 * während der Lead im Cockpit etwas ändert) darf nie zu einem Fehler oder
 * einem leeren Bildschirm führen — die Bühne fällt dann auf die neutrale
 * Pausenfolie zurück, wie überall sonst auch (`PauseSlide.tsx`).
 */
export function AgendaItemSlide({ snapshot }: { snapshot: StageSnapshot }) {
  const { t } = useTranslation();
  const item = snapshot.agendaItems[snapshot.agendaIndex];
  if (!item) return <PauseSlide />;

  return (
    <div className="stage-slide">
      <h1 className="stage-title">{item.title}</h1>
      <div
        className="stage-agenda-body"
        dangerouslySetInnerHTML={{ __html: sanitizeForDisplay(item.bodyHtml) }}
      />
      <p className="stage-sub">
        {t("stage.agendaOf", {
          n: snapshot.agendaIndex + 1,
          total: snapshot.agendaItems.length,
        })}
      </p>
    </div>
  );
}
